import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { api, type ArtefactSummary, type TopicSummary } from '../lib/api.ts'
import { belongsToDepartment, departmentBySlug } from '../lib/departments.ts'
import { buildEntries, buildFacets } from '../lib/entries.ts'
import { Breadcrumbs } from './Breadcrumbs.tsx'
import { FilterBar } from './FilterBar.tsx'
import { ToolCard } from './ToolCard.tsx'

export function DepartmentPage() {
  const { dept = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const [artefacts, setArtefacts] = useState<ArtefactSummary[]>([])
  const [topics, setTopics] = useState<TopicSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const department = departmentBySlug(dept)

  useEffect(() => {
    Promise.all([api.catalog(), api.topics()])
      .then(([a, t]) => {
        setArtefacts(a)
        setTopics(t)
      })
      .catch(() => setError('Could not load this department. Please try again.'))
      .finally(() => setLoading(false))
  }, [])

  const qa = params.get('qa')
  const type = params.get('type')
  const isFiltered = Boolean(qa || type)

  // belongsToDepartment decides membership at the artefact level, same as
  // before; a topic survives here only if one of its members belongs to this
  // department — buildEntries drops any topic with zero matching members, so
  // there's no separate "does this topic belong to the department" rule to
  // keep in sync (that duplication was Task 11's bug).
  const { visible, facets, total } = useMemo(() => {
    if (!department) return { visible: [], facets: { qa: [], type: [] }, total: 0 }
    const deptArtefacts = artefacts.filter((a) => belongsToDepartment(a, department))
    return {
      visible: buildEntries(deptArtefacts, topics, { qa, type }),
      facets: buildFacets(deptArtefacts, topics, { qa, type }),
      total: buildEntries(deptArtefacts, topics).length,
    }
  }, [artefacts, topics, department, qa, type])

  const clearFilters = () => {
    const next = new URLSearchParams(params)
    next.delete('qa')
    next.delete('type')
    setParams(next, { replace: true })
  }

  if (!department) return <main className="catalog"><p className="notice">Unknown department.</p></main>

  return (
    <main className="catalog">
      <Breadcrumbs items={[{ label: 'Departments', to: '/' }, { label: department.name }]} />

      <header className="dept__head">
        {/* No "Department" eyebrow: the trail above already says which part of
            the office this is, and the heading says which one. */}
        <h1 className="tool__title">{department.name}</h1>
        {/* The count is what tells you a two-card page is the whole story
            rather than something still loading. */}
        {!error && !loading && (
          <p className="tool__lead">
            {isFiltered
              ? `Showing ${visible.length} of ${total} ${total === 1 ? 'item' : 'items'}.`
              : `${total} ${total === 1 ? 'item' : 'items'}.`}
          </p>
        )}
      </header>

      <FilterBar qa={facets.qa} type={facets.type} />

      {error && <p className="notice notice--error">{error}</p>}

      {loading && !error && <p className="notice">Loading…</p>}

      {/* Only claim an empty result once loading has actually finished —
          otherwise this flashes on every visit before the catalog arrives. */}
      {!error && !loading && visible.length === 0 && (
        <div className="empty">
          {/* Two different empty states. A department Astute has not written
              content for yet is not the same as a filter that excludes all of
              it — telling someone there is nothing here when a chip they set is
              hiding it would send them away from content that exists. */}
          <p className="empty__lead">
            {isFiltered
              ? 'Nothing matches these filters.'
              : 'No content as of yet, please check back later!'}
          </p>
          {isFiltered && (
            <button type="button" className="btn btn--soft" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      )}

      <div className="catalog__grid">
        {visible.map((e) => (
          <ToolCard
            key={e.id}
            id={e.id}
            to={e.to}
            department={e.department}
            // Every card here shares the page's department, so the Quality Area
            // is the label that actually tells them apart.
            eyebrow={e.qualityArea || e.department}
            title={e.title}
            isDraft={e.isDraft}
            composition={e.composition}
            // A bundle describes itself through its composition line. A tool
            // has no contents to describe, so it says what it gets you —
            // "Tool" on its own tells a director nothing. Anything else falls
            // back to naming its content type.
            description={e.kind === 'artefact' && e.meta === 'Tool'
              ? 'Fill in a short form to generate a document.'
              : undefined}
            meta={e.kind === 'artefact' && e.meta !== 'Tool' ? e.meta : undefined}
          />
        ))}
      </div>
    </main>
  )
}
